// Entries of encrypted libraries (docs/STORAGE.md §3): each file's metadata (path, type,
// tags, and the keys of its blobs) is encrypted under the library key; the server keeps it
// as an opaque string next to the ciphertext blobs.

import type { BlobRole, LibraryEntry } from '../api/types'
import type { MediaKeyInfo } from './attachments'
import { decryptName, encryptName } from './metacrypto'
import type { CopyMode } from './playbackCopy'

export type EntryKind = 'audio' | 'video' | 'image' | 'file'

/** A blob's key material, plus its type when it isn't the entry's own (art, previews). */
export interface BlobKey extends MediaKeyInfo {
  mime?: string
}

export interface EntryTags {
  title?: string
  artist?: string
  album?: string
  track?: number
  /** Seconds. */
  duration?: number
  /** `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. */
  release_date?: string
  label?: string
  composers?: string[]
  producers?: string[]
  /** Videos: a few lines about it. */
  description?: string
  /** Photos: when it was taken, `YYYY`, `YYYY-MM` or `YYYY-MM-DD`. */
  taken?: string
}

/** Whether a release date looks like 1977, 1977-05 or 1977-05-25 (a real date). */
export function validReleaseDate(d: string): boolean {
  const m = /^(\d{4})(?:-(\d{2})(?:-(\d{2}))?)?$/.exec(d)
  if (!m) return false
  const [y, mo, da] = [Number(m[1]), m[2] ? Number(m[2]) : 1, m[3] ? Number(m[3]) : 1]
  if (mo < 1 || mo > 12) return false
  const days = new Date(Date.UTC(y, mo, 0)).getUTCDate()
  return da >= 1 && da <= days
}

/** Folders and files directly inside `folder` (`''` for the top), from every entry's path. */
export function listFolder<T extends { meta: EntryMeta }>(rows: T[], folder: string): { folders: string[]; files: T[] } {
  const prefix = folder ? `${folder}/` : ''
  const folders = new Set<string>()
  const files: T[] = []
  for (const r of rows) {
    if (!r.meta.path.startsWith(prefix)) continue
    const rest = r.meta.path.slice(prefix.length)
    const slash = rest.indexOf('/')
    if (slash < 0) files.push(r)
    else folders.add(rest.slice(0, slash))
  }
  return {
    folders: [...folders].sort((a, b) => a.localeCompare(b)),
    files: files.sort((a, b) => a.meta.path.localeCompare(b.meta.path)),
  }
}

/** Audio grouped into albums (artist — album), tracks in order. */
export function albumsOf<T extends { meta: EntryMeta }>(rows: T[]): { artist: string; album: string; tracks: T[] }[] {
  const by = new Map<string, { artist: string; album: string; tracks: T[] }>()
  for (const r of rows) {
    if (r.meta.kind !== 'audio') continue
    const artist = r.meta.tags?.artist ?? 'Unknown artist'
    const album = r.meta.tags?.album ?? 'Singles'
    const k = `${artist}\u0000${album}`
    if (!by.has(k)) by.set(k, { artist, album, tracks: [] })
    by.get(k)!.tracks.push(r)
  }
  for (const a of by.values()) {
    a.tracks.sort((x, y) => (x.meta.tags?.track ?? 0) - (y.meta.tags?.track ?? 0) || x.meta.path.localeCompare(y.meta.path))
  }
  return [...by.values()].sort((a, b) => a.artist.localeCompare(b.artist) || a.album.localeCompare(b.album))
}

export type SortBy = 'name' | 'added' | 'size'

/** The title shown for an entry: its tag, else the file name. */
export const entryTitle = (m: EntryMeta) => m.tags?.title ?? fileName(m.path)

/** Entries sorted for a list: by name (title), newest first, or largest first. */
export function sortRows<T extends { meta: EntryMeta }>(rows: T[], by: SortBy): T[] {
  const out = [...rows]
  if (by === 'added') return out.sort((a, b) => b.meta.added - a.meta.added)
  if (by === 'size') return out.sort((a, b) => b.meta.size - a.meta.size)
  return out.sort((a, b) => entryTitle(a.meta).localeCompare(entryTitle(b.meta)))
}

/** Whether an entry matches a search, across its path and tags. */
export function matches(meta: EntryMeta, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const t = meta.tags ?? {}
  return [meta.path, t.title, t.artist, t.album, t.label, t.description, ...(t.composers ?? []), ...(t.producers ?? [])]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
    .includes(q)
}

export interface EntryMeta {
  v: 1
  /** Folder path and file name, e.g. `Albums/Kind of Blue/01 So What.flac`. */
  path: string
  /** Plaintext bytes of the original. */
  size: number
  mime: string
  kind: EntryKind
  /** Browsers play the original as it is (no streamable copy needed). */
  streamable: boolean
  /**
   * Why there's a `stream` copy: not every browser plays the original, or it's a lighter
   * alternative to an original they do play. Copies from before this was kept: 'playable'.
   */
  copy?: 'playable' | 'lighter'
  /** When it was imported (ms). */
  added: number
  /** The collection it's in (an entry id, HRTH-164.6); '' for none, even where its tags name an album. */
  in?: string
  tags?: EntryTags
  /** Keys of the entry's blobs, by role; the blobs themselves are listed by the server. */
  blobs: Partial<Record<BlobRole, BlobKey>>
}

const kind = (library: string) => `library-entry:${library}` as const

export async function encryptEntryMeta(key: string, library: string, meta: EntryMeta): Promise<string> {
  return encryptName(key, kind(library), JSON.stringify(meta))
}

/** The entry's metadata, or null if it doesn't open with this key or isn't a file entry. */
export async function decryptEntryMeta(key: string, library: string, entry: LibraryEntry): Promise<EntryMeta | null> {
  const json = await decryptName(key, kind(library), entry.meta)
  if (!json || json === entry.meta) return null
  try {
    const m = JSON.parse(json) as EntryMeta
    return m.v === 1 && typeof m.path === 'string' ? m : null
  } catch {
    return null
  }
}

/** The file name of an entry's path. */
export function fileName(path: string): string {
  return path.split('/').pop() ?? path
}

/** Settings everyone with a library's key shares; only admins change them. */
export interface LibrarySettings {
  /** The default for playback copies when uploading. */
  copies?: CopyMode
}

const settingsKind = (library: string) => `library-settings:${library}` as const

export async function readLibrarySettings(key: string | undefined, library: string, sealed?: string): Promise<LibrarySettings> {
  if (!key || !sealed) return {}
  const json = await decryptName(key, settingsKind(library), sealed)
  if (!json || json === sealed) return {}
  try {
    const s = JSON.parse(json) as unknown
    return s && typeof s === 'object' ? (s as LibrarySettings) : {}
  } catch {
    return {}
  }
}

export function sealLibrarySettings(key: string, library: string, settings: LibrarySettings): Promise<string> {
  return encryptName(key, settingsKind(library), JSON.stringify(settings))
}
